import React, { useEffect, useLayoutEffect, useRef } from "react";
import { connect } from "react-redux";
import { CinemaType, MovieType } from "../../types/entities";
import { getDateFilterDisplayString } from "../../utils/showtimes";
import ShowtimeButton from "../common/Showtimes/ShowtimeButton";
import Button from "../Button";
import { getBootstrapFilters, getCheckoutCinema, getCheckoutMovie } from "../../reducers";
import { CHECKOUT_OTHER_SHOWTIMES_FETCH } from "../../actions/types";
import { getCheckoutOtherShowtimes } from "../../reducers/checkout/otherShowtimes";
import styled from "styled-components";

const Scroller = styled.div`
    position: relative;
    overflow-x: auto;
    width: 100%;
    user-select: none;
    margin-bottom: 4rem;
    
    &::-webkit-scrollbar {
        width: 0.5rem;
        height: 0.5rem;
    }

    &::-webkit-scrollbar-track { background: #fcfcfc; /* Color of the track */ }
    &::-webkit-scrollbar-thumb { background: #eee; /* Color of the thumb */ }
    &::-webkit-scrollbar-thumb:hover { background: #ddd; /* Darker color on hover */ }

    body.theme-market & { 
        &::-webkit-scrollbar-track { background: #333; /* Color of the track */ }
        &::-webkit-scrollbar-thumb { background: #666; /* Color of the thumb */ }
        &::-webkit-scrollbar-thumb:hover { background: #777; /* Darker color on hover */ }
    }

    body.theme-platinum & { 
        &::-webkit-scrollbar-track { background: var(--primary-b); /* Color of the track */ }
        &::-webkit-scrollbar-thumb { background: var(--primary); /* Color of the thumb */ }
        &::-webkit-scrollbar-thumb:hover { background: var(--primary); /* Darker color on hover */ }
    }
`;

const DayGrid = styled.div`
    display: flex;
    gap: 0.5rem;
    width: max-content;
`;

const DayBlock = styled.div`
    display: inline-flex;
    align-items: stretch;
    gap: 0.5rem;
    position: relative;
    min-width: max-content;
`;

const StickyDayButton = styled.div`
    display: flex;
    align-items: center;
    background: var(--page-bg);
    height: 100%;
    left: 0;
    position: sticky;
    z-index: 2;

    & button {
        height: 100%;
    }
`;

const ShowtimeButtonWrapper = styled.div`
    display: inline-flex;
    align-items: stretch;

    & > div {
        display: flex;
        align-items: center;
        height: 100%;
    }

    & button, & a {
        height: 100%;
    }
`;

interface Props {
    attributes: { [key: string]: { display_name: string } };
    cinema: CinemaType;
    currentSessionId: number;
    data?: { day: string; sessions: any[] }[] | null;
    fetch: () => void;
    movie: MovieType;
}

function ShowtimesSelector(props: Props) {
    const [langs, setLangs] = React.useState<string[]>([]);
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const buttonRefs = useRef<{ [id: string]: HTMLDivElement | null }>({});
    const setScrollerRef = (node: HTMLDivElement | null) => { scrollerRef.current = node; };

    useEffect(() => {
        const hasData = !!props.data;

        if (hasData || !props.cinema || !props.movie) {
            return;
        }
        
        props.fetch();
    }, [props.cinema, props.movie, props.data]);

    useLayoutEffect(() => {
        if (!props.currentSessionId || !props.data || !props.data.length) {
            return;
        }

        const scroller = scrollerRef.current;
        const button = buttonRefs.current[props.currentSessionId];

        if (!scroller || !button) {
            return;
        }

        const scrollerRect = scroller.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();
        const currentScroll = scroller.scrollLeft;
        const buttonCenter = buttonRect.left - scrollerRect.left + currentScroll + buttonRect.width / 2;
        const targetScroll = Math.max(0, buttonCenter - scrollerRect.width / 2);
        scroller.scrollTo({ left: targetScroll });
    }, [props.currentSessionId, props.data]);

    useEffect(() => {
        if (!props.movie) {
            return;
        }

        let attributes = Array.isArray(props.movie.attributes) ? props.movie.attributes : [];
        let langs = attributes.filter(attr => attr.startsWith('lang_'));
        setLangs(langs);
    }, [props.movie]);

    if (!props.data || !props.data.length) {
        return null;
    }

    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    return <>
        {getTitle(props.attributes, langs, props.movie, props.cinema)}
        <div className="showtimes-selector">
            <Scroller innerRef={setScrollerRef}>
                <DayGrid>
                    {props.data.map(dayData => (
                        <DayBlock key={dayData.day}>
                            <StickyDayButton>
                                <Button primary={dayData.day === today} secondary={dayData.day !== today}>{getDateFilterDisplayString(dayData.day)}</Button>
                            </StickyDayButton>
                            {dayData.sessions.map(item => (
                                <ShowtimeButtonWrapper
                                    key={item.session.id}
                                    innerRef={node => { buttonRefs.current[item.session.id] = node; }}
                                >
                                    <ShowtimeButton session={item.session} version={item.version} movieName={props.movie.name} cinema={props.cinema} disabled={item.session.id === props.currentSessionId} />
                                </ShowtimeButtonWrapper>
                            ))}
                        </DayBlock>
                    ))}
                </DayGrid>
            </Scroller>
        </div>
    </>;
}

function getTitle(attributes: { [key: string]: { display_name: string } }, langs: string[], movie: MovieType, cinema: CinemaType) {
    if (!langs.length) {
        return null;
    }

    const displayNames = langs.map(lang => attributes[lang] ? attributes[lang].display_name : lang);
    return <p className="mb-3">Otras sesiones <strong>({movie.name} - {displayNames.join(', ')} - {cinema.name})</strong></p>;
}

const mapStateToProps = (state: any) => ({
    attributes: getBootstrapFilters(state),
    data: getCheckoutOtherShowtimes(state),
    movie: getCheckoutMovie(state),
    cinema: getCheckoutCinema(state),
});

const mapDispatchToProps = (dispatch: any) => ({
    fetch: () => dispatch({ type: CHECKOUT_OTHER_SHOWTIMES_FETCH }),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ShowtimesSelector);